import { supabase } from './supabase';
import type { Category, Product, TechnicalSpec } from './schema';
import { adminApi } from './api';

// URL base para las Edge Functions del marketplace
const MARKETPLACE_API_URL = 'https://jlufgkgssnxumguvknhc.functions.supabase.co';

// Token de autenticación para las Edge Functions
const API_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpsZWZna2dzc254dW1ndXZrbmhjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MDk4MjI0MDAsImV4cCI6MjAyNTM5ODQwMH0.dkF2QHwHu2Rl2yZQDVTqS_FWzOhGBZGJXYZKUcF0QXE';

// Configuración del bucket de Supabase para imágenes
const SUPABASE_STORAGE_URL = 'https://jlufgkgssnxumguvknhc.supabase.co/storage/v1';
const BUCKET_NAME = 'marketplace-used';

// Headers comunes para todas las peticiones
const getCommonHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${API_KEY}`,
});

// Opciones comunes para fetch
const getCommonFetchOptions = (method: string = 'GET', body?: any) => ({
  method,
  headers: getCommonHeaders(),
  body: body ? JSON.stringify(body) : undefined,
  mode: 'cors' as RequestMode,
  credentials: 'omit' as RequestCredentials,
});

// Interfaz para los parámetros de búsqueda y filtrado
export interface ListingFilters {
  query?: string;
  price_min?: number;
  price_max?: number;
  brand?: string[];
  category_id?: number[];
  location?: string[];
  sort?: 'newest' | 'price_asc' | 'price_desc';
  page?: number;
  limit?: number;
}

// NUEVA INTERFAZ PARA CATEGORÍAS CON HIJOS
export interface CategoryWithChildren {
  id: number;
  name: string;
  slug: string;
  parent_id: number | null; // Asegúrate que el nombre coincida con tu DB, puede ser parentId
  description?: string;
  // Otros campos que vengan de tu tabla marketplace_categories
  children?: CategoryWithChildren[];
}

// Interfaz para objetos de tipo listing
export interface MarketplaceListing {
  id: string;
  title: string;
  description: string;
  price: number;
  brand: string;
  images: string[];
  condition: string;
  category_id: string;
  location?: string;
  year?: number;
  is_active: boolean;
  features: string[];
  technical_specs: TechnicalSpec[];
  created_at: string;
  pdf_url?: string;
  marketplace_categories?: {
    id: string;
    name: string;
    slug: string;
  };
}

// Interfaz para la respuesta paginada
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Manejador de errores unificado
const handleApiError = (error: any, fallbackMessage: string, returnFallback?: any) => {
  let errorMessage = fallbackMessage;
  if (error instanceof Error) {
    errorMessage = error.message;
  }
  console.error(`Error API: ${errorMessage}`, error);

  // Si hay un valor de retorno por defecto, lo devolvemos en caso de error
  if (returnFallback !== undefined) {
    return returnFallback;
  }

  // Si no hay valor por defecto, lanzamos el error
  throw new Error(errorMessage);
};

// Función para obtener los listados con filtros
export async function getMarketplaceListings(filters: ListingFilters = {}): Promise<PaginatedResponse<MarketplaceListing>> {
  try {
    // Construir URL con parámetros de filtro
    const url = new URL(`${MARKETPLACE_API_URL}/marketplace-used/listings`);

    // Añadir parámetros a la URL
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        let paramKey = key;
        // Renombrar 'query' a 'search' para la API
        if (key === 'query') {
          paramKey = 'search';
        }

        if (Array.isArray(value)) {
          url.searchParams.append(paramKey, value.join(','));
        } else {
          url.searchParams.append(paramKey, String(value));
        }
      }
    });

    const response = await fetch(url.toString(), getCommonFetchOptions());

    if (!response.ok) {
      throw new Error(`Error al obtener listados: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, 'Error al obtener listados', {
      data: [],
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
      }
    });
  }
}

// Función para obtener un listado específico por ID
export async function getMarketplaceListing(id: string): Promise<MarketplaceListing> {
  try {
    const fetchUrl = `${MARKETPLACE_API_URL}/marketplace-used/listings/${id}`;
    console.log("Fetching marketplace listing from:", fetchUrl);
    const response = await fetch(
      fetchUrl,
      getCommonFetchOptions()
    );

    if (!response.ok) {
      throw new Error(`Error al obtener el listado: ${response.statusText}`);
    }

    const listing = await response.json();
    console.log("Fetched listing data:", listing); // Log para depurar
    return listing;
  } catch (error) {
    return handleApiError(error, 'Error al obtener el listado');
  }
}

// Función para obtener categorías
export async function getMarketplaceCategories(): Promise<Category[]> {
  try {
    const response = await fetch(
      `${MARKETPLACE_API_URL}/marketplace-used/categories`,
      getCommonFetchOptions()
    );

    if (!response.ok) {
      throw new Error(`Error al obtener categorías: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, 'Error al obtener categorías', []);
  }
}

// Función para obtener la jerarquía de categorías
export async function getMarketplaceCategoriesHierarchy(): Promise<CategoryWithChildren[]> {
  try {
    const response = await fetch(
      `${MARKETPLACE_API_URL}/marketplace-used/categories/hierarchy`,
      getCommonFetchOptions()
    );

    if (!response.ok) {
      throw new Error(`Error al obtener jerarquía de categorías: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, 'Error al obtener jerarquía de categorías', []);
  }
}

// Función para enviar formulario de contacto
export async function sendMarketplaceContactRequest(data: {
  listing_id: string;
  name: string;
  email: string;
  phone?: string;
  message: string;
}): Promise<{ success: boolean }> {
  try {
    const response = await fetch(
      `${MARKETPLACE_API_URL}/marketplace-used/contact`,
      getCommonFetchOptions('POST', data)
    );

    if (!response.ok) {
      throw new Error(`Error al enviar solicitud de contacto: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, 'Error al enviar solicitud de contacto');
  }
}

// Función para búsqueda avanzada
export async function searchMarketplaceListings(searchParams: {
  query?: string;
  category?: string;
  min_price?: number;
  max_price?: number;
  page?: number;
  limit?: number;
}): Promise<PaginatedResponse<MarketplaceListing>> {
  try {
    // Construir URL con parámetros de búsqueda
    const url = new URL(`${MARKETPLACE_API_URL}/marketplace-used-search`);

    // Añadir parámetros a la URL
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });

    const response = await fetch(url.toString(), getCommonFetchOptions());

    if (!response.ok) {
      throw new Error(`Error en la búsqueda: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, 'Error en la búsqueda');
  }
}

// Función para obtener estadísticas
export async function getMarketplaceStats(): Promise<{
  total_active_listings: number;
  category_distribution: Record<string, number>;
  price_statistics: {
    avg_price: number;
    min_price: number;
    max_price: number;
  };
}> {
  try {
    const response = await fetch(
      `${MARKETPLACE_API_URL}/marketplace-used-stats`,
      getCommonFetchOptions()
    );

    if (!response.ok) {
      throw new Error(`Error al obtener estadísticas: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, 'Error al obtener estadísticas');
  }
}

// Función para crear un nuevo listado (ADMIN)
export async function createMarketplaceListing(data: Omit<MarketplaceListing, 'id' | 'created_at'>): Promise<MarketplaceListing> {
  try {
    const response = await fetch(
      `${MARKETPLACE_API_URL}/marketplace-used/listings`,
      getCommonFetchOptions('POST', data)
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error al crear listado: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, 'Error al crear listado');
  }
}

// Función para actualizar un listado existente (ADMIN)
export async function updateMarketplaceListing(id: string, data: Partial<MarketplaceListing>): Promise<MarketplaceListing> {
  try {
    const response = await fetch(
      `${MARKETPLACE_API_URL}/marketplace-used/listings/${id}`,
      getCommonFetchOptions('PUT', data)
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error al actualizar listado: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, 'Error al actualizar listado');
  }
}

// Función para eliminar un listado (ADMIN)
export async function deleteMarketplaceListing(id: string): Promise<{ success: boolean }> {
  try {
    const response = await fetch(
      `${MARKETPLACE_API_URL}/marketplace-used/listings/${id}`,
      getCommonFetchOptions('DELETE')
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error al eliminar listado: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, 'Error al eliminar listado');
  }
}

// Función para crear una nueva categoría (ADMIN)
export async function createMarketplaceCategory(data: {
  name: string;
  slug?: string;
  parent_id?: string | null;
  description?: string;
}): Promise<Category> {
  try {
    const response = await fetch(
      `${MARKETPLACE_API_URL}/marketplace-used/categories`,
      getCommonFetchOptions('POST', data)
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error al crear categoría: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, 'Error al crear categoría');
  }
}

// Función para actualizar una categoría existente (ADMIN)
export async function updateMarketplaceCategory(id: string, data: {
  name?: string;
  slug?: string;
  parent_id?: string | null;
  description?: string;
}): Promise<Category> {
  try {
    const response = await fetch(
      `${MARKETPLACE_API_URL}/marketplace-used/categories/${id}`,
      getCommonFetchOptions('PUT', data)
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error al actualizar categoría: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, 'Error al actualizar categoría');
  }
}

// Función para eliminar una categoría (ADMIN)
export async function deleteMarketplaceCategory(id: string): Promise<{ success: boolean }> {
  try {
    const response = await fetch(
      `${MARKETPLACE_API_URL}/marketplace-used/categories/${id}`,
      getCommonFetchOptions('DELETE')
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error al eliminar categoría: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, 'Error al eliminar categoría');
  }
}

// NUEVA FUNCIÓN GENÉRICA PARA SUBIR ARCHIVOS
export async function uploadFile(
  file: File,
  folder: string = 'uploads',
  allowedTypes: string[] = ['application/pdf'],
  maxSizeMB: number = 10
): Promise<string> {
  try {
    // Validar tipo de archivo
    if (!allowedTypes.includes(file.type)) {
      throw new Error(`Tipo de archivo no permitido. Permitidos: ${allowedTypes.join(', ')}`);
    }

    // Validar tamaño de archivo
    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error(`El archivo no debe superar los ${maxSizeMB}MB`);
    }

    // Crear un FormData para enviar el archivo
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder); // Enviar la carpeta de destino

    // La función de Supabase debería estar preparada para recibir 'folder'
    // y usarlo para construir la ruta de subida.
    // Usaremos la misma función de subida de imágenes pero adaptada.
    const response = await fetch(
      `${MARKETPLACE_API_URL}/marketplace-used/storage/upload`,
      {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${API_KEY}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Error al subir archivo: ${response.statusText}`);
    }

    const data = await response.json();
    return data.path; // Devuelve la ruta del archivo
  } catch (error) {
    return handleApiError(error, 'Error al subir archivo');
  }
}

// Función para subir una imagen al storage (AHORA USA uploadFile)
export async function uploadMarketplaceImage(file: File): Promise<string> {
  return uploadFile(
    file,
    'products',
    ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    5 // 5MB para imágenes
  );
}

// Función para obtener la URL pública de un archivo
export function getPublicImageUrl(filePath: string, folder?: string): string {
  // Si ya es una URL completa, devolverla
  if (!filePath || filePath.startsWith('http')) {
    return filePath || '';
  }

  // Si no, construir la URL completa. Usar la carpeta si se especifica.
  const fullPath = folder ? `${folder}/${filePath.split('/').pop()}` : filePath;

  return `${SUPABASE_STORAGE_URL}/object/public/${BUCKET_NAME}/${fullPath}`;
}

// Función para validar si una URL de imagen es válida
export function isValidImageUrl(url: string | undefined): boolean {
  if (!url) return false;
  if (url.trim() === '' || url.includes('undefined') || url.includes('null')) {
    return false;
  }

  // Si es una ruta interna, considerarla válida
  if (url.startsWith('/')) return true;

  // Intentar validar la URL
  try {
    new URL(url);
    return true;
  } catch (e) {
    return url.startsWith('/') || url.startsWith('./') || url.startsWith('../');
  }
}

// Función para obtener una URL alternativa si la original falla
export function getAlternativeImageUrl(imageUrl: string): string | null {
  try {
    // Si no es una URL de Supabase, devolver la misma
    if (imageUrl.startsWith('http') && !imageUrl.includes('supabase.co')) {
      return imageUrl;
    }

    // Extraer el nombre del archivo
    const fileName = imageUrl.split('/').pop() || '';

    // Construir URL directa de Supabase
    return `${SUPABASE_STORAGE_URL}/object/public/${BUCKET_NAME}/products/${fileName}`;
  } catch (error) {
    console.error('Error al generar URL alternativa:', error);
    return null;
  }
}

